import { ChangeEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, List, Modal } from 'antd';

import { useUserUnitsMenusa } from 'entities/user/model/userUserUnitsMenusa';
import { EmptyState } from 'shared/ui/EmptyState/EmptyState';
import { MpButton } from 'shared/ui/MpButton';
import { ConnectMenusaStyled, ModalTitleStyled } from './ConnectMenusa.styles';

interface Props {
	isInstalled: boolean;
	handleInstall?: () => Promise<void>;
}

export const ConnectMenusa: FC<Props> = ({ isInstalled, handleInstall }) => {
	const [isOpen, setIsOpen] = useState(false);
	const { userUnits } = useUserUnitsMenusa();
	const [filtered, setFiltered] = useState(false);
	const [searchValue, setSearchValue] = useState(``);
	const { t } = useTranslation();
	const open = (): void => {
		setIsOpen(true);
	};

	const close = (): void => {
		setIsOpen(false);
	};

	const onSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
		setFiltered(e.target.value !== ``);
		setSearchValue(e.target.value);
	};

	return (
		<ConnectMenusaStyled>
			{isInstalled ? (
				<div>
					<MpButton block type='primary' size='large' onClick={open}>
						{t(`Marketplace_Download_QR_Menu_Button`)}
					</MpButton>
				</div>
			) : (
				<MpButton block type='primary' size='large' onClick={handleInstall}>
					{t(`Marketplace_Install`)}
				</MpButton>
			)}
			<Modal
				className='otvb-modal'
				bodyStyle={{ borderRadius: `16px` }}
				open={isOpen}
				onCancel={close}
				footer={null}
			>
				<ModalTitleStyled>{t(`Marketplace_Download_QR_Menu`)}</ModalTitleStyled>
				<Input
					style={{ marginBottom: `40px`, borderRadius: `8px`, fontSize: `14px` }}
					size='large'
					onChange={onSearchChange}
					value={searchValue}
					prefix={<SearchOutlined style={{ fontSize: `16px` }} />}
					allowClear
					placeholder={t(`Marketplace_Search`) ?? `Поиск`}
				/>
				{filtered &&
				[...userUnits]
					.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()))
					.filter(n => n.name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())).length === 0 ? (
					<EmptyState
						heading={t(`Marketplace_Menusa_Empty_State_Heading`)}
						description={t(`Marketplace_Menusa_Empty_State_Description`)}
						image='https://cdn.dodostatic.net/static/marketplace/menusa/empty'
					/>
				) : (
					<List
						dataSource={
							filtered
								? [...userUnits]
										.sort((a, b) =>
											a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()),
										)
										.filter(n =>
											n.name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()),
										)
								: [...userUnits].sort((a, b) =>
										a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()),
								  )
						}
						style={{
							borderRadius: `8px`,
							maxHeight: `640px`,
							overflowY: `scroll`,
							overflowX: `hidden`,
						}}
						renderItem={item => (
							<List.Item
								extra={
									// eslint-disable-next-line jsx-a11y/control-has-associated-label
									<a
										target='_blank'
										rel='noreferrer'
										href={`https://menusa.app/api/get-qr/${item.id.replace(
											/(.{8})(.{4})(.{4})(.{4})(.{12})/g,
											`$1-$2-$3-$4-$5`,
										)}/${item.name}.pdf?width=360&height=490&tenant=dodopizza&countryCode=${
											item.countryCode
										}`}
									>
										<Button
											size='large'
											style={{ borderRadius: `8px` }}
											icon={<DownloadOutlined />}
										/>
									</a>
								}
							>
								{item.name}
							</List.Item>
						)}
					/>
				)}
			</Modal>
		</ConnectMenusaStyled>
	);
};
